import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { Badge, Button, Card, Modal, Select, Textarea, useToast } from '@nexora/design-system';

/* ── Contrato ──────────────────────────────────────────────────────────────
 * `GET /api/v1/orders/:id` → `SalesOrderResponseDto`
 *   (sales/presentation/http/dto/sales.dto.ts:234). Campos usados aqui:
 *   id, number, customerId, customerName, status, items[{id, productId,
 *   description, quantity, unitValue, discount, discountType, taxValues[],
 *   total, type}], subtotal, discountTotal, taxTotal, grandTotal, conditions,
 *   cfop, paymentTerms, fiscalDocumentId, createdAt, approvalStatus,
 *   approvals[], financial { dueDate }.
 *
 * `GET /api/v1/orders/:id/approvals` → `OrderApprovalHistoryResponseDto`
 *   (sales.dto.ts:407): { orderId, number, status,
 *   approval { status, requiredLevels, approvedLevels, pendingLevels,
 *   lastAction, lastAt, lastBy }, history[{ id, action, approverId,
 *   approverName, level, comment, at, ruleId, requiredLevels }] }.
 *
 * Mutações reais do fluxo (sales-orders.controller.ts):
 *   POST /orders/:id/submit  { comment?, requiredLevels?, ruleId? }
 *   PATCH /orders/:id/approve { comment?, level? }
 *   PATCH /orders/:id/reject  { reason }
 *
 * O item do pedido NÃO expõe SKU: o prisma guarda `productId`. A coluna
 * "Código" da referência é renderizada com o id do produto abreviado (mesmo
 * expediente da lista de pedidos), nunca inventando campo.
 */

interface OrderTaxValues {
  [taxName: string]: number;
}

interface OrderItemDetail {
  id: string;
  productId: string;
  description: string;
  quantity: number;
  unitValue: number;
  discount: number;
  discountType: string;
  taxValues: OrderTaxValues;
  total: number;
  type: string;
}

interface OrderDetail {
  id: string;
  number: string;
  customerId: string;
  customerName: string | null;
  status: string;
  items: OrderItemDetail[];
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  conditions: string;
  cfop: string;
  paymentTerms: string;
  fiscalDocumentId: string | null;
  createdAt: string;
  approvalStatus: string | null;
  approvals: OrderApprovalEntry[];
  financial: { dueDate: string | null } | null;
}

interface OrderApprovalEntry {
  id: string;
  action: string;
  approverId: string;
  approverName: string | null;
  level: number;
  comment: string | null;
  at: string;
  ruleId: string | null;
  requiredLevels: number | null;
}

interface OrderApprovalSummary {
  status: string;
  requiredLevels: number;
  approvedLevels: number;
  pendingLevels: number;
  lastAction: string | null;
  lastAt: string | null;
  lastBy: string | null;
}

interface OrderApprovalHistory {
  orderId: string;
  number: string;
  status: string;
  approval: OrderApprovalSummary;
  history: OrderApprovalEntry[];
}

const STATUS_VARIANT: Record<string, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  DRAFT: 'neutral',
  CONFIRMED: 'info',
  INVOICED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  CONFIRMED: 'Confirmado',
  INVOICED: 'Faturado',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

const APPROVAL_VARIANT: Record<string, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  NOT_REQUIRED: 'neutral',
};

const APPROVAL_LABEL: Record<string, string> = {
  PENDING: 'Pendente de aprovação',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  NOT_REQUIRED: 'Aprovação não exigida',
};

const ACTION_LABEL: Record<string, string> = {
  SUBMITTED: 'Enviado para aprovação',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  CREATED: 'Criado',
};

/* Niveis de alçada sugeridos para o envio. O backend aceita 0 (confirma
   direto) — a tela parte de 1, o default da regra. */
const LEVELS = [1, 2, 3];

function nivelLabel(level: number): string {
  return `${level}º nível`;
}

export function SalesOrderApprovalPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const params = useParams({ strict: false }) as { saleId?: string };
  const orderId = params.saleId;

  const [modal, setModal] = useState<'submit' | 'approve' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');
  const [level, setLevel] = useState('1');
  const [requiredLevels, setRequiredLevels] = useState('1');

  const orderQ = useQuery({
    queryKey: ['sales', 'orders', orderId ?? 'sem-id'],
    queryFn: async () => apiClient.get<OrderDetail>(`/orders/${orderId}`),
    enabled: !!orderId,
  });

  const approvalQ = useQuery({
    queryKey: ['sales', 'orders', orderId ?? 'sem-id', 'approvals'],
    queryFn: async () => apiClient.get<OrderApprovalHistory>(`/orders/${orderId}/approvals`),
    enabled: !!orderId,
  });

  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: ['sales', 'orders', orderId ?? 'sem-id'] })
      .catch(console.error);
    queryClient
      .invalidateQueries({ queryKey: ['sales', 'orders', orderId ?? 'sem-id', 'approvals'] })
      .catch(console.error);
  };

  const submitMutation = useMutation({
    mutationFn: () =>
      apiClient.post<OrderDetail>(`/orders/${orderId}/submit`, {
        comment: comment.trim() ? comment.trim() : undefined,
        requiredLevels: Number(requiredLevels),
      }),
    onSuccess: () => {
      addToast('Pedido enviado para aprovação', 'success');
      setModal(null);
      setComment('');
      invalidate();
    },
    onError: (err) => addToast((err as Error).message ?? 'Erro ao enviar para aprovação', 'error'),
  });

  const approveMutation = useMutation({
    mutationFn: () =>
      apiClient.patch<OrderDetail>(`/orders/${orderId}/approve`, {
        comment: comment.trim() ? comment.trim() : undefined,
        level: Number(level),
      }),
    onSuccess: () => {
      addToast('Pedido aprovado', 'success');
      setModal(null);
      setComment('');
      invalidate();
    },
    onError: (err) => addToast((err as Error).message ?? 'Erro ao aprovar pedido', 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      apiClient.patch<OrderDetail>(`/orders/${orderId}/reject`, {
        reason: reason.trim(),
      }),
    onSuccess: () => {
      addToast('Pedido rejeitado', 'success');
      setModal(null);
      setReason('');
      invalidate();
    },
    onError: (err) => addToast((err as Error).message ?? 'Erro ao rejeitar pedido', 'error'),
  });

  const order = orderQ.data;
  const approval = approvalQ.data;
  const summary = approval?.approval;

  /* Total de impostos exibidos por item, quando o pedido os publica. */
  const taxSummary = useMemo(() => {
    if (!order) return [];
    return order.items.map((item) => ({
      id: item.id,
      taxes: Object.entries(item.taxValues ?? {}).map(([name, value]) => ({ name, value })),
    }));
  }, [order]);

  const podeEnviar = order?.status === 'DRAFT' && (order.approvalStatus ?? null) !== 'APPROVED';
  const podeAprovar = order?.status === 'DRAFT' && (order.approvalStatus ?? null) !== 'APPROVED';
  const podeRejeitar = order?.status === 'DRAFT' && (order.approvalStatus ?? null) !== 'REJECTED';

  const openModal = (which: 'submit' | 'approve' | 'reject'): void => {
    setComment('');
    setReason('');
    setModal(which);
  };

  const confirmModal = (e: FormEvent): void => {
    e.preventDefault();
    if (modal === 'submit') submitMutation.mutate();
    if (modal === 'approve') approveMutation.mutate();
    if (modal === 'reject' && reason.trim()) rejectMutation.mutate();
  };

  const modalTitle =
    modal === 'submit'
      ? 'Enviar para aprovação'
      : modal === 'approve'
        ? 'Aprovar pedido'
        : modal === 'reject'
          ? 'Rejeitar pedido'
          : '';

  const hasApprovalFlow = !!summary || (order?.approvals.length ?? 0) > 0;

  return (
    <div className="sales-approval-page" style={{ padding: '24px' }}>
      {/* ── Cabeçalho ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-md mb-lg">
        <div>
          <div className="flex items-center gap-sm flex-wrap">
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">
              {order?.number
                ? `Pedido ${order.number}`
                : order
                  ? 'Pedido de venda'
                  : 'Pedido de venda'}
            </h1>
            {order ? (
              <Badge variant={STATUS_VARIANT[order.status] ?? 'neutral'}>
                {STATUS_LABEL[order.status] ?? order.status}
              </Badge>
            ) : null}
            {order?.approvalStatus ? (
              <Badge variant={APPROVAL_VARIANT[order.approvalStatus] ?? 'neutral'}>
                {APPROVAL_LABEL[order.approvalStatus] ?? order.approvalStatus}
              </Badge>
            ) : null}
          </div>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Fluxo de aprovação, histórico e termos comerciais do pedido.
          </p>
        </div>
        {order && hasApprovalFlow ? (
          <div className="flex flex-wrap gap-sm">
            {podeEnviar && order.approvals.length === 0 && (
              <Button variant="outline" size="sm" onClick={() => openModal('submit')}>
                Enviar para aprovação
              </Button>
            )}
            {podeAprovar && (order.approvals.length > 0 || summary) && (
              <Button variant="primary" size="sm" onClick={() => openModal('approve')}>
                Aprovar
              </Button>
            )}
            {podeRejeitar && (order.approvals.length > 0 || summary) && (
              <Button variant="danger" size="sm" onClick={() => openModal('reject')}>
                Rejeitar
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {orderQ.isLoading ? (
        <div className="space-y-md">
          <Card>
            <p className="font-body-md text-body-md text-on-surface-variant">Carregando pedido…</p>
          </Card>
        </div>
      ) : orderQ.isError || !order ? (
        <Card>
          <p className="font-body-md text-body-md text-error mb-md" role="alert">
            {orderQ.isError
              ? (orderQ.error as Error).message ?? 'Pedido não encontrado'
              : 'Informe o identificador do pedido na rota.'}
          </p>
          {orderQ.isError && orderId ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Verifique se o pedido existe e se o identificador está correto.
            </p>
          ) : null}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg">
            {/* ── Coluna principal: detalhes + itens ── */}
            <div className="lg:col-span-2 flex flex-col gap-lg">
              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary mb-md">
                  Customer Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Cliente
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {order.customerName ?? <code>{order.customerId.slice(0, 8)}</code>}
                    </p>
                  </div>
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Identificador do cliente
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      <code>{order.customerId}</code>
                    </p>
                  </div>
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Emissão
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Documento fiscal
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {order.fiscalDocumentId ? (
                        <code>{order.fiscalDocumentId}</code>
                      ) : (
                        'não faturado'
                      )}
                    </p>
                  </div>
                </div>
              </Card>

              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary mb-md">
                  Commercial Terms
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Condição de pagamento
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {order.paymentTerms || '—'}
                    </p>
                  </div>
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      CFOP
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">{order.cfop || '—'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Referência
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {order.conditions || '—'}
                    </p>
                  </div>
                  {order.financial?.dueDate ? (
                    <div>
                      <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                        Vencimento
                      </span>
                      <p className="font-body-md text-body-md text-on-surface">
                        {formatDate(order.financial.dueDate)}
                      </p>
                    </div>
                  ) : null}
                </div>
              </Card>

              <Card>
                <div className="flex items-center justify-between mb-md">
                  <h3 className="font-headline-sm text-headline-sm text-primary m-0">Line Items</h3>
                  <span className="font-label-md text-label-md text-on-surface-variant">
                    {order.items.length} {order.items.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low border-b border-outline-variant font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">
                        <th className="p-sm font-medium">Produto</th>
                        <th className="p-sm font-medium text-right">Qtd</th>
                        <th className="p-sm font-medium text-right">V. Unit</th>
                        <th className="p-sm font-medium text-right">Desc.</th>
                        <th className="p-sm font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="font-body-md text-body-md text-on-surface">
                      {order.items.map((item) => (
                        <tr
                          key={item.id}
                          className="border-b border-surface-container hover:bg-surface-container-lowest transition-colors"
                        >
                          <td className="p-sm">
                            <span className="block font-label-md text-label-md text-primary">
                              {item.productId.slice(0, 8)}
                            </span>
                            <span className="block font-body-sm text-body-sm text-on-surface-variant">
                              {item.description}
                            </span>
                          </td>
                          <td className="p-sm text-right">{item.quantity}</td>
                          <td className="p-sm text-right">{formatCurrency(item.unitValue)}</td>
                          <td className="p-sm text-right text-error">
                            {item.discount > 0 ? `- ${formatCurrency(item.discount)}` : '—'}
                          </td>
                          <td className="p-sm text-right font-bold">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {order.items.length > 0 && itemTaxRows(order).length > 0 ? (
                  <div className="mt-md border-t border-surface-container pt-sm">
                    <span className="block font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-xs">
                      Tributos por item
                    </span>
                    {taxSummary.map((row) =>
                      row.taxes.length > 0 ? (
                        <div key={row.id} className="flex flex-wrap gap-md py-xs">
                          <span className="font-label-md text-label-md text-primary">
                            {order.items.find((i) => i.id === row.id)?.productId.slice(0, 8)}
                          </span>
                          {row.taxes.map((t) => (
                            <span key={t.name} className="font-body-sm text-body-sm text-on-surface-variant">
                              {t.name}: {formatCurrency(t.value)}
                            </span>
                          ))}
                        </div>
                      ) : null,
                    )}
                  </div>
                ) : null}
              </Card>
            </div>

            {/* ── Coluna lateral: totais + aprovação ── */}
            <div className="flex flex-col gap-lg">
              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary mb-md">Totais</h3>
                <div className="space-y-sm">
                  <div className="flex justify-between font-body-md text-body-md text-on-surface-variant">
                    <span>Subtotal</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between font-body-md text-body-md text-error">
                    <span>Desconto</span>
                    <span>- {formatCurrency(order.discountTotal)}</span>
                  </div>
                  {order.taxTotal > 0 ? (
                    <div className="flex justify-between font-body-md text-body-md text-on-surface-variant">
                      <span>Tributos</span>
                      <span>+ {formatCurrency(order.taxTotal)}</span>
                    </div>
                  ) : null}
                  <div className="flex justify-between font-headline-md text-headline-md text-on-surface pt-sm border-t border-surface-container">
                    <span>Total</span>
                    <span>{formatCurrency(order.grandTotal)}</span>
                  </div>
                </div>
              </Card>

              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary flex items-center gap-xs mb-md">
                  <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                    history
                  </span>
                  Histórico de Aprovação
                </h3>
                {summary ? (
                  <div className="mb-md space-y-xs">
                    <div className="flex justify-between items-center">
                      <span className="font-label-md text-label-md text-on-surface-variant">
                        Situação
                      </span>
                      <Badge variant={APPROVAL_VARIANT[summary.status] ?? 'neutral'}>
                        {APPROVAL_LABEL[summary.status] ?? summary.status}
                      </Badge>
                    </div>
                    {summary.lastAt ? (
                      <div className="flex justify-between items-center">
                        <span className="font-label-md text-label-md text-on-surface-variant">
                          Última ação
                        </span>
                        <span className="font-body-sm text-body-sm text-on-surface">
                          {ACTION_LABEL[summary.lastAction ?? ''] ?? summary.lastAction}
                          {summary.lastBy ? ` · ${summary.lastBy}` : ''} · {formatDate(summary.lastAt)}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex justify-between items-center">
                      <span className="font-label-md text-label-md text-on-surface-variant">
                        Alçada
                      </span>
                      <span className="font-body-sm text-body-sm text-on-surface">
                        {summary.approvedLevels}/{summary.requiredLevels} níveis
                        {summary.pendingLevels > 0 ? ` (${summary.pendingLevels} pendente)` : ''}
                      </span>
                    </div>
                  </div>
                ) : null}

                {approvalQ.isLoading ? (
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    Carregando histórico…
                  </p>
                ) : (
                  <div className="flex flex-col gap-sm">
                    {(approval?.history ?? order.approvals).length === 0 ? (
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        Nenhuma aprovação registrada até o momento.
                      </p>
                    ) : (
                      (approval?.history ?? order.approvals).map((entry) => (
                        <div
                          key={entry.id}
                          className="flex flex-col border-l-2 border-outline-variant pl-sm py-xs"
                        >
                          <span className="font-label-sm text-label-sm text-on-surface font-bold">
                            {entry.approverName ?? entry.approverId.slice(0, 8)}
                            {entry.level > 1 ? ` · ${nivelLabel(entry.level)}` : ''}
                          </span>
                          <span className="font-body-sm text-body-sm text-on-surface-variant">
                            {formatDate(entry.at)}
                          </span>
                          {entry.action ? (
                            <Badge
                              variant={
                                entry.action === 'APPROVED'
                                  ? 'success'
                                  : entry.action === 'REJECTED'
                                    ? 'error'
                                    : 'info'
                              }
                              size="sm"
                              className="mt-xs w-fit"
                            >
                              {ACTION_LABEL[entry.action] ?? entry.action}
                            </Badge>
                          ) : null}
                          {entry.comment ? (
                            <span className="font-body-sm text-body-sm text-on-surface-variant italic mt-xs">
                              “{entry.comment}”
                            </span>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </Card>
            </div>
          </div>
        </>
      )}

      {/* ── Modais de ação do fluxo ── */}
      <Modal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        title={modalTitle}
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button
              variant={modal === 'reject' ? 'danger' : 'primary'}
              size="sm"
              loading={submitMutation.isPending || approveMutation.isPending || rejectMutation.isPending}
              disabled={modal === 'reject' && !reason.trim()}
              onClick={confirmModal}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <form onSubmit={confirmModal} className="space-y-md">
          {modal === 'submit' ? (
            <div className="space-y-md">
              <Select
                label="Níveis de alçada exigidos"
                value={requiredLevels}
                onChange={(e) => setRequiredLevels(e.target.value)}
              >
                {LEVELS.map((n) => (
                  <option key={n} value={String(n)}>
                    {nivelLabel(n)}
                  </option>
                ))}
              </Select>
              <Textarea
                label="Comentário (opcional)"
                placeholder="Justificativa ou observação para a aprovação"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
              />
            </div>
          ) : null}
          {modal === 'approve' ? (
            <div className="space-y-md">
              <Select label="Nível da alçada" value={level} onChange={(e) => setLevel(e.target.value)}>
                {LEVELS.map((n) => (
                  <option key={n} value={String(n)}>
                    {nivelLabel(n)}
                  </option>
                ))}
              </Select>
              <Textarea
                label="Comentário (opcional)"
                placeholder="Parecer do aprovador"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
              />
            </div>
          ) : null}
          {modal === 'reject' ? (
            <Textarea
              label="Motivo da rejeição (obrigatório)"
              placeholder="Descreva o motivo para devolver o pedido"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          ) : null}
        </form>
      </Modal>
    </div>
  );
}

/** Linhas de tributo por item, para a seção "Tributos por item". */
function itemTaxRows(order: OrderDetail): Array<{ id: string; total: number }> {
  return order.items
    .map((item) => ({
      id: item.id,
      total: Object.values(item.taxValues ?? {}).reduce((s, v) => s + v, 0),
    }))
    .filter((row) => row.total > 0);
}